import { Table, Tag, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import type { ColumnType } from "antd/es/table";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { actions as routerActions } from "redux-router5";
import type { Services, Turbine, TicketInProject } from "../generated-axios-client";
import { AssigneeAvatarGroup } from "../../common/components/AssigneeAvatarGroup";
import { EditableDatePicker } from "../../common/components/EditableDatePicker";
import { NoData } from "../../common/components/NoDataComp";
import { ReportDoneBadge } from "../../common/components/ReportDoneBadge";
import { TableActions } from "../../common/components/TableActions";
import { projectActions } from "../../features/project/projectSlice";
import { routeNames } from "../common/routes";
import { statusColorMap, statusLabelMap } from "../ticket/ticketConstants";
import { SortableDragRow, DragHandle } from "./SortableDragRow";
import { useDragSort } from "./useDragSort";

type Props = {
  tickets: TicketInProject[];
  projectId: string;
  intl: IntlShape;
  dispatch: Dispatch;
  serviceObjects: Record<string, Services> | null;
  turbines: Turbine[] | undefined;
};

const buildEditableDateColumn = (
  field: "start" | "deadline",
  titleMessageId: string,
  intl: IntlShape,
  dispatch: Dispatch,
  projectId: string,
): ColumnType => ({
  key: field,
  dataIndex: field,
  title: intl.formatMessage({ id: titleMessageId }),
  render: (value: string | null, record: unknown) => {
    const ticketId = (record as TicketInProject).id;
    return (
      <EditableDatePicker
        variableName={field}
        variableValue={value}
        intl={intl}
        onChange={
          ticketId
            ? (_variableName, isoValue) =>
                dispatch(
                  projectActions.projectTicketDateEdit({
                    projectId,
                    ticketId,
                    field,
                    value: isoValue,
                  }),
                )
            : undefined
        }
      />
    );
  },
});

const buildColumns = (
  intl: IntlShape,
  serviceObjects: Record<string, Services> | null,
  turbineMap: Record<string, string>,
  savingId: string | null,
  dispatch: Dispatch,
  projectId: string,
): ColumnType[] => [
  {
    key: "drag",
    dataIndex: "drag",
    title: "",
    width: 40,
    render: (_: unknown, record: unknown) => {
      const ticket = record as TicketInProject;
      return <DragHandle saving={savingId === ticket.id} />;
    },
  },
  {
    key: "ticket_number",
    dataIndex: "ticket_number",
    title: intl.formatMessage({ id: "table_column_ticket_number" }),
    render: (value: number | null, record: unknown) => {
      if (value == null) return <NoData intl={intl} />;
      const ticket = record as TicketInProject;
      return ticket.id ? (
        <Typography.Link
          strong
          onClick={() =>
            dispatch(
              routerActions.navigateTo(routeNames.TICKET_VIEW, {
                id: ticket.id,
              }),
            )
          }
        >
          {`#${value}`}
        </Typography.Link>
      ) : (
        <Typography.Text strong type="secondary">
          {`#${value}`}
        </Typography.Text>
      );
    },
  },
  {
    key: "service",
    dataIndex: "service",
    title: intl.formatMessage({ id: "table_column_service" }),
    render: (value: string | null) => {
      const label = value ? serviceObjects?.[value]?.name : null;
      return label ?? <NoData intl={intl} />;
    },
  },
  {
    key: "report",
    dataIndex: "report_done",
    title: intl.formatMessage({ id: "table_column_report" }),
    width: 130,
    render: (value: boolean | null | undefined) => (
      <ReportDoneBadge reportDone={value} />
    ),
  },
  {
    key: "assignee",
    dataIndex: "assignees",
    title: intl.formatMessage({ id: "table_column_assignee" }),
    render: (assignees: TicketInProject["assignees"]) => (
      <AssigneeAvatarGroup assignees={(assignees ?? []).map((a) => a.assignee)} />
    ),
  },
  {
    key: "turbine",
    dataIndex: "turbine",
    title: intl.formatMessage({ id: "table_column_turbine" }),
    render: (value: string | null) => {
      const serialNumber = value ? turbineMap[value] : null;
      return serialNumber ?? <NoData intl={intl} />;
    },
  },
  buildEditableDateColumn("start", "table_column_start", intl, dispatch, projectId),
  buildEditableDateColumn(
    "deadline",
    "table_column_deadline",
    intl,
    dispatch,
    projectId,
  ),
  {
    key: "status",
    dataIndex: "status",
    title: intl.formatMessage({ id: "table_column_status" }),
    render: (value: string | null) =>
      value && value in statusLabelMap ? (
        <Tag
          color={value in statusColorMap ? statusColorMap[value] : "gray"}
          variant="outlined"
          styles={{ root: { fontWeight: 700 } }}
        >
          {statusLabelMap[value]}
        </Tag>
      ) : (
        <NoData intl={intl} />
      ),
  },
  {
    key: "actions",
    title: intl.formatMessage({ id: "table_column_actions" }),
    width: 80,
    render: () => null,
  },
];

export const ProjectWorkOrdersTable = ({
  tickets,
  projectId,
  intl,
  dispatch,
  serviceObjects,
  turbines,
}: Props) => {
  const { sortedTickets, savingId, onDragEnd } = useDragSort(tickets, projectId, dispatch);

  const turbineMap: Record<string, string> = turbines
    ? Object.fromEntries(turbines.map((t) => [t.id, t.serial_number]))
    : {};

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const columns = buildColumns(
    intl,
    serviceObjects,
    turbineMap,
    savingId,
    dispatch,
    projectId,
  ).map((col) => {
    if (col.key === "actions") {
      return {
        ...col,
        render: (_: unknown, record: unknown) => {
          const ticket = record as TicketInProject;
          return (
            <TableActions
              actions={[
                {
                  key: "view",
                  icon: <EyeOutlined />,
                  tooltip: "View Work Order",
                  onClick: () =>
                    dispatch(
                      routerActions.navigateTo(routeNames.TICKET_VIEW, {
                        id: ticket.id,
                      }),
                    ),
                },
              ]}
            />
          );
        },
      };
    }
    return col;
  });

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext
        items={sortedTickets.map((t) => t.id ?? "")}
        strategy={verticalListSortingStrategy}
      >
        <Table
          columns={columns}
          dataSource={sortedTickets}
          rowKey={(record) => (record as TicketInProject).id ?? ""}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: "No work orders for this project" }}
          size="small"
          components={{ body: { row: SortableDragRow } }}
        />
      </SortableContext>
    </DndContext>
  );
};
